
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, X } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

const AutoSelectedQuestions = ({ 
  selectedQuestions, 
  onRemoveQuestion, 
  onViewAll 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const questionsPerPage = 10;

  if (selectedQuestions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No questions auto-selected yet
      </div>
    );
  }

  const getQuestionTypeBadge = (qtyp) => {
    const typeMap = {
      'SINGLE_CHOICE': 'Single Choice',
      'MULTIPLE_CHOICE': 'Multiple Choice',
      'TRUE_FALSE': 'True/False',
      'SHORT_ANSWER': 'Short Answer',
      'ESSAY': 'Essay',
      'FILL_IN_BLANK': 'Fill in Blank'
    };
    return typeMap[qtyp] || qtyp?.replace('_', ' ') || 'Unknown';
  };

  const totalPages = Math.ceil(selectedQuestions.length / questionsPerPage);
  const startIndex = (currentPage - 1) * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const currentQuestions = selectedQuestions.slice(startIndex, endIndex);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">
          Auto-Selected Questions ({selectedQuestions.length})
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
        {currentQuestions.map((question, index) => (
          <Card key={question._id} className="relative">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle className="text-sm">
                  Question {startIndex + index + 1}
                </CardTitle>
                {onRemoveQuestion && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveQuestion(question._id)}
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                <p className="text-sm line-clamp-2">{question.question}</p>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="outline" className="text-xs">
                    {question.category}
                  </Badge>
                  {question.subCategory && (
                    <Badge variant="outline" className="text-xs">
                      {question.subCategory}
                    </Badge>
                  )}
                  <Badge variant="outline" className={`text-xs capitalize ${
                    question.difficultyLevel === 'easy' ? 'bg-green-100 text-green-800' :
                    question.difficultyLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {question.difficultyLevel}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {getQuestionTypeBadge(question.qtyp)}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-6">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className={
                  currentPage === 1
                    ? "pointer-events-none opacity-50"
                    : "cursor-pointer"
                }
              />
            </PaginationItem>
            {(() => {
              const totalPages = Math.ceil(selectedQuestions.length / questionsPerPage);
              const current = currentPage;
              let start = Math.max(1, current - 2);
              let end = Math.min(totalPages, current + 2);

              if (end - start < 4) {
                if (start === 1) {
                  end = Math.min(5, totalPages);
                } else if (end === totalPages) {
                  start = Math.max(1, totalPages - 4);
                }
              }

              const pages = [];
              for (let i = start; i <= end; i++) {
                pages.push(
                  <PaginationItem key={i}>
                    <PaginationLink
                      isActive={currentPage === i}
                      onClick={() => setCurrentPage(i)}
                      className="cursor-pointer"
                    >
                      {i}
                    </PaginationLink>
                  </PaginationItem>
                );
              }
              return pages;
            })()}
            <PaginationItem>
              <PaginationNext
                onClick={() =>
                  setCurrentPage((p) =>
                    Math.min(p + 1, Math.ceil(selectedQuestions.length / questionsPerPage))
                  )
                }
                disabled={currentPage === Math.ceil(selectedQuestions.length / questionsPerPage)}
                className={
                  currentPage === Math.ceil(selectedQuestions.length / questionsPerPage)
                    ? "pointer-events-none opacity-50"
                    : "cursor-pointer"
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};

export default AutoSelectedQuestions;
